// Lightweight datetime helpers — Russian locale + DD.MM.YYYY default
const RU_MONTHS = [
  'января','февраля','марта','апреля','мая','июня',
  'июля','августа','сентября','октября','ноября','декабря',
];
const RU_WEEKDAYS_FULL = ['воскресенье','понедельник','вторник','среда','четверг','пятница','суббота'];
const RU_WEEKDAYS_SHORT = ['вс','пн','вт','ср','чт','пт','сб'];
const RU_MONTHS_SHORT = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];

const EMPTY_DT = '—';

/**
 * Map i18n.language to Intl locale for general UI.
 * English uses en-GB so numeric and medium dates are day-first (ISSUE-0133).
 */
export function uiIntlLocale(lang) {
  return String(lang || '').startsWith('ru') ? 'ru-RU' : 'en-GB';
}

/** Coerce any display locale to day-first English (or keep Russian). */
export function dayFirstIntlLocale(locale) {
  const s = String(locale || '');
  if (s.startsWith('ru')) return s.startsWith('ru-') ? s : 'ru-RU';
  return 'en-GB';
}

function _ymdInTz(iso, tz) {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: tz });
}

/** UTC instant → date+time in the user's locale (no timezone shift). */
export function formatInstantDateTime(iso, locale = 'ru-RU', { long = false } = {}) {
  if (!iso) return EMPTY_DT;
  const loc = dayFirstIntlLocale(locale);
  return new Intl.DateTimeFormat(loc, {
    day: '2-digit',
    month: long ? 'long' : '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** UTC instant → short month + time (queue session rows). */
export function formatInstantDateTimeShort(iso, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

/** UTC instant → time only. */
export function formatInstantTime(iso, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

/** Collapse same-day end to time-only (poll list confirmed banner). */
export function formatInstantRange(startIso, endIso, locale = 'ru-RU') {
  if (!startIso) return EMPTY_DT;
  const start = formatInstantDateTime(startIso, locale);
  if (!endIso) return start;
  const sameDay = new Date(startIso).toDateString() === new Date(endIso).toDateString();
  const endLabel = sameDay
    ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(endIso))
    : formatInstantDateTime(endIso, locale);
  return `${start}–${endLabel}`;
}

/** Zoned date+time for a UTC ISO instant. */
export function formatZonedDateTime(iso, tz, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(new Date(iso));
}

export function formatZonedTime(iso, tz, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(new Date(iso));
}

export function formatZonedWeekdayDate(iso, tz, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    weekday: 'short', day: 'numeric', month: 'long', timeZone: tz,
  }).format(new Date(iso));
}

/** Poll vote slot range — same-day end collapses to time; multi-day uses arrow. */
export function formatZonedRange(startIso, endIso, tz, locale = 'ru-RU') {
  if (!startIso) return EMPTY_DT;
  const start = formatZonedDateTime(startIso, tz, locale);
  if (!endIso) return start;
  if (_ymdInTz(startIso, tz) === _ymdInTz(endIso, tz)) {
    const endT = formatZonedTime(endIso, tz, locale);
    return `${start}–${endT}`;
  }
  return `${start} → ${formatZonedDateTime(endIso, tz, locale)}`;
}

/** UTC instant → short month + time in a planning timezone (queue When rows). */
export function formatZonedDateTimeShort(iso, tz, locale = 'ru-RU') {
  if (!iso) return EMPTY_DT;
  return new Intl.DateTimeFormat(dayFirstIntlLocale(locale), {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: tz || 'UTC',
  }).format(new Date(iso));
}

/**
 * ISSUE-0140 — Queue dashboard When/subtitle: same planning-TZ day collapses
 * end to time-only; different day includes end date (short month).
 */
export function formatZonedRangeShort(startIso, endIso, tz, locale = 'ru-RU') {
  if (!startIso) return EMPTY_DT;
  const zone = tz || 'UTC';
  const start = formatZonedDateTimeShort(startIso, zone, locale);
  if (!endIso) return start;
  if (_ymdInTz(startIso, zone) === _ymdInTz(endIso, zone)) {
    return `${start} – ${formatZonedTime(endIso, zone, locale)}`;
  }
  return `${start} – ${formatZonedDateTimeShort(endIso, zone, locale)}`;
}

// Compact date-tile parts ({ weekday, day, month }) for a prominent date block.
export function dateTileParts(iso, tz, locale = 'ru-RU') {
  const d = new Date(iso);
  const ca = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  }).formatToParts(d);
  const dayNum = parseInt(ca.find((p) => p.type === 'day')?.value || '1', 10);
  const moNum = parseInt(ca.find((p) => p.type === 'month')?.value || '1', 10) - 1;
  const yrNum = parseInt(ca.find((p) => p.type === 'year')?.value || '0', 10);
  const wkEn = ca.find((p) => p.type === 'weekday')?.value || '';
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  if (locale.startsWith('ru')) {
    return { weekday: RU_WEEKDAYS_SHORT[map[wkEn] ?? 0], day: dayNum, month: RU_MONTHS_SHORT[moNum], year: yrNum };
  }
  return { weekday: wkEn, day: dayNum, month: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short' }).format(d), year: yrNum };
}

function _parts(iso, tz) {
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
  }).formatToParts(d);
  const get = (t) => p.find(x => x.type === t)?.value || '';
  return {
    y: get('year'), m: get('month'), dd: get('day'),
    HH: get('hour') === '24' ? '00' : get('hour'),
    MM: get('minute'),
  };
}

export function formatDateRU(iso, tz) {
  const { y, m, dd } = _parts(iso, tz);
  return `${dd}.${m}.${y}`;
}

export function formatTime(iso, tz, locale = 'ru-RU') {
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

export function formatDateTimeRU(iso, tz) {
  const { y, m, dd, HH, MM } = _parts(iso, tz);
  return `${dd}.${m}.${y}, ${HH}:${MM}`;
}

export function formatFriendlyDateRU(iso, tz, locale = 'ru-RU') {
  if (locale.startsWith('ru')) {
    const d = new Date(iso);
    // Get day & month-num in tz
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' });
    const parts = fmt.formatToParts(d);
    const day = parts.find(p => p.type === 'day')?.value;
    const mo = parseInt(parts.find(p => p.type === 'month')?.value, 10) - 1;
    return `${parseInt(day,10)} ${RU_MONTHS[mo]}`;
  }
  return new Intl.DateTimeFormat(locale, { timeZone: tz, day: 'numeric', month: 'long' }).format(new Date(iso));
}

export function formatWeekdayDate(iso, tz, locale = 'ru-RU') {
  if (locale.startsWith('ru')) {
    const d = new Date(iso);
    const wkParts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).formatToParts(d);
    const wk = wkParts.find(p => p.type === 'weekday')?.value || '';
    const map = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };
    const wkRu = RU_WEEKDAYS_FULL[map[wk] ?? 0];
    return `${wkRu}, ${formatFriendlyDateRU(iso, tz, locale)}`;
  }
  return new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(iso));
}

export function formatInTz(iso, tz, opts = {}) {
  // Backward-compat wrapper used by older callers — returns 'DD.MM.YYYY, HH:mm' in RU
  const locale = opts.locale || 'ru-RU';
  if (locale.startsWith('ru')) return formatDateTimeRU(iso, tz);
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz, year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

// Returns naive local ISO 'YYYY-MM-DDTHH:mm:ss' for the given timezone (no offset/Z).
export function isoLocalInTz(iso, tz) {
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t) => p.find((x) => x.type === t)?.value || '00';
  const HH = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${HH}:${get('minute')}:${get('second')}`;
}

export function getHourInTz(iso, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const h = parts.find((p) => p.type === 'hour');
  const v = parseInt(h?.value || '0', 10);
  return v === 24 ? 0 : v;
}

export function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// Keep these for backward-compat with existing imports
export function formatDate(iso, tz, locale = 'ru-RU') {
  return formatFriendlyDateRU(iso, tz, locale);
}

// --- Timezone-aware day boundaries (used by Calendar Overview) ---
// Minutes that `tz` is ahead of UTC at the given instant.
export function tzOffsetMinutes(date, tz) {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
  // Round to the nearest minute: `date` may carry milliseconds that `asUTC`
  // (truncated to whole seconds) does not, which would otherwise yield a
  // fractional offset like 179.9926 → "UTC+2:59.99…".
  return Math.round((asUTC - date.getTime()) / 60000);
}

// UTC Date instant corresponding to local midnight of y-mo(0-based)-d in `tz`.
export function zonedDayStart(y, mo, d, tz) {
  const guess = new Date(Date.UTC(y, mo, d, 0, 0, 0));
  const off = tzOffsetMinutes(guess, tz);
  return new Date(guess.getTime() - off * 60000);
}

// Convert a `datetime-local` wall-clock string ('YYYY-MM-DDTHH:mm[:ss]') that the
// user entered intending it in `tz` into the correct absolute UTC Date instant.
// (A bare `new Date(str)` would interpret it in the BROWSER's tz — wrong when the
// host's timezone differs from the device's.)
export function wallTimeToUtc(localStr, tz) {
  if (!localStr) return null;
  const [datePart, timePart = '00:00'] = String(localStr).split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  if (!y || !mo || !d) return null;
  const guess = new Date(Date.UTC(y, mo - 1, d, h || 0, mi || 0, 0));
  const off = tzOffsetMinutes(guess, tz);
  return new Date(guess.getTime() - off * 60000);
}

// ISSUE-0028 Rev 1: zero-tolerance future-start check after wall→UTC conversion.
// A start equal to now is treated as past (must be strictly later).
export function isUtcStartInPast(isoOrDate, nowMs = Date.now()) {
  if (isoOrDate == null || isoOrDate === '') return false;
  const t = isoOrDate instanceof Date ? isoOrDate.getTime() : new Date(isoOrDate).getTime();
  return Number.isFinite(t) && t <= nowMs;
}

export function anyUtcStartInPast(starts, nowMs = Date.now()) {
  return (starts || []).some((s) => isUtcStartInPast(s, nowMs));
}

function _hhmmToMinutes(hhmm) {
  const [h, m] = String(hhmm || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function _minutesToHhmm(total) {
  const clamped = Math.max(0, Math.min(24 * 60, total));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Wall-clock { ymd, hhmm } for `nowMs` in planning timezone `tz`. */
export function planningNowWall(tz, nowMs = Date.now()) {
  const local = isoLocalInTz(new Date(nowMs).toISOString(), tz || 'UTC');
  return { ymd: local.slice(0, 10), hhmm: local.slice(11, 16) };
}

/** Earliest selectable calendar day in the planning timezone (today there). */
export function minPlanningDateStr(tz, nowMs = Date.now()) {
  return planningNowWall(tz, nowMs).ymd;
}

/**
 * Earliest selectable start time chip for `dateYmd` in planning timezone.
 * Future days → '00:00'. Today → first stepMinutes grid point strictly after now.
 * If no chip remains today → '24:00' (TimeField yields an empty grid).
 */
export function minPlanningStartTime(dateYmd, tz, nowMs = Date.now(), stepMinutes = 30) {
  const { ymd, hhmm } = planningNowWall(tz, nowMs);
  if (!dateYmd || dateYmd > ymd) return '00:00';
  if (dateYmd < ymd) return '24:00';
  const step = Math.max(5, stepMinutes || 30);
  const next = Math.ceil((_hhmmToMinutes(hhmm) + 1) / step) * step;
  if (next >= 24 * 60) return '24:00';
  return _minutesToHhmm(next);
}

function _minutesToHhmmModulo(total) {
  const day = 24 * 60;
  const wrapped = ((Math.round(Number(total) || 0) % day) + day) % day;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * True when end clock is on or before start clock, so the meeting wraps to
 * the next planning-timezone calendar day (at most one night).
 */
export function isOvernightEnd(startHhmm, endHhmm) {
  if (!startHhmm || !endHhmm) return false;
  return _hhmmToMinutes(endHhmm) <= _hhmmToMinutes(startHhmm);
}

/** End `YYYY-MM-DD` for a start date + start/end clocks (wraps at most one day). */
export function endYmdAfterStart(startYmd, startHhmm, endHhmm) {
  if (!startYmd) return '';
  if (!isOvernightEnd(startHhmm, endHhmm)) return startYmd;
  const next = addCalendarDaysYmd(startYmd, 1);
  return next || startYmd;
}

/**
 * UTC instants for a same-meeting start/end clock pair on `dateYmd`.
 * End clock on or before start clock is stored on the next calendar day.
 */
export function wallRangeToUtc(dateYmd, startHhmm, endHhmm, tz) {
  if (!dateYmd || !startHhmm) return { start: null, end: null };
  const start = wallTimeToUtc(`${dateYmd}T${startHhmm}`, tz);
  if (!endHhmm) return { start, end: null };
  const endYmd = endYmdAfterStart(dateYmd, startHhmm, endHhmm);
  const end = wallTimeToUtc(`${endYmd}T${endHhmm}`, tz);
  return { start, end };
}

/**
 * First HH:MM chip strictly after `startHhmm` (same-day TimeField min).
 * Pass `{ nextDay: true }` when the end is already on the next calendar day
 * so the grid starts at 00:00 instead of the empty same-day sentinel 24:00.
 */
export function minEndTimeAfterStart(startHhmm, stepMinutes = 30, extra = {}) {
  const opts = (stepMinutes && typeof stepMinutes === 'object')
    ? stepMinutes
    : { stepMinutes, ...extra };
  if (opts.nextDay) return '00:00';
  if (!startHhmm) return '00:00';
  const step = Math.max(5, opts.stepMinutes || 30);
  const next = Math.ceil((_hhmmToMinutes(startHhmm) + 1) / step) * step;
  if (next >= 24 * 60) return '24:00';
  return _minutesToHhmm(next);
}

/**
 * First same-day end chip strictly after start and not past maxEnd.
 * Returns '24:00' (empty TimeField grid) when no later same-day end exists.
 */
export function minSameDayEndAfterStart(startHhmm, stepMinutes = 30, maxEnd = '23:30') {
  if (!startHhmm) return '00:00';
  const startM = _hhmmToMinutes(startHhmm);
  const maxM = _hhmmToMinutes(maxEnd);
  if (!(maxM > startM)) return '24:00';
  const stepped = minEndTimeAfterStart(startHhmm, stepMinutes);
  if (stepped === '24:00' || _hhmmToMinutes(stepped) > maxM) return maxEnd;
  return stepped;
}

/** Latest start chip that still leaves room for a later same-day end ≤ maxEnd. */
export function maxSameDayStart(maxEnd = '23:30', stepMinutes = 30) {
  const maxM = _hhmmToMinutes(maxEnd);
  const step = Math.max(5, stepMinutes || 30);
  const last = Math.floor((maxM - 1) / step) * step;
  return _minutesToHhmm(Math.max(0, last));
}

/**
 * Same-day availability window: keep a later end, otherwise the first valid
 * end after start. Returns '' when start leaves no room before maxEnd.
 */
export function coerceSameDayEndAfterStart(startHhmm, endHhmm, stepMinutes = 30, maxEnd = '23:59') {
  if (endHhmm && _hhmmToMinutes(endHhmm) > _hhmmToMinutes(startHhmm)
      && _hhmmToMinutes(endHhmm) <= _hhmmToMinutes(maxEnd)) {
    return endHhmm;
  }
  const next = minSameDayEndAfterStart(startHhmm, stepMinutes, maxEnd);
  return next === '24:00' ? '' : next;
}

/** True when start leaves no later same-day end chip ≤ maxEnd (e.g. 23:30). */
export function needsDateRangeForLateStart(startHhmm, stepMinutes = 30, maxEnd = '23:30') {
  return !!startHhmm && minSameDayEndAfterStart(startHhmm, stepMinutes, maxEnd) === '24:00';
}

/**
 * Same-day end after start for Start + duration mode. Keeps a later same-day
 * end; otherwise start + durationMinutes when that still fits ≤ maxEnd.
 * If duration would cross midnight / maxEnd, falls back to the first same-day
 * chip after start. Returns '' when no later same-day end exists (e.g. 23:30)
 * — callers should guide hosts to Date range mode.
 */
export function sameDayEndAfterStart(startHhmm, {
  durationMinutes = 30,
  stepMinutes = 30,
  existingEnd = '',
  maxEnd = '23:30',
} = {}) {
  if (!startHhmm) return existingEnd || '';
  const startM = _hhmmToMinutes(startHhmm);
  const maxM = _hhmmToMinutes(maxEnd);
  if (existingEnd) {
    const endM = _hhmmToMinutes(existingEnd);
    if (endM > startM && endM <= maxM) return existingEnd;
  }
  if (!(maxM > startM)) return '';
  const step = Math.max(5, stepMinutes || 30);
  const dur = Math.max(step, Number(durationMinutes) || 30);
  const endM = startM + dur;
  if (endM <= maxM) return _minutesToHhmm(endM);
  return coerceSameDayEndAfterStart(startHhmm, '', stepMinutes, maxEnd);
}

/**
 * Auto end after start. Keeps a later same-day end, or an existing next-day
 * end; otherwise start + durationMinutes, wrapping to the next day when the
 * sum crosses local midnight (at most one night).
 */
export function endWallAfterStart(startHhmm, {
  durationMinutes = 30,
  stepMinutes = 30,
  existingEnd = '',
  existingIsNextDay = false,
} = {}) {
  if (!startHhmm) {
    return { hhmm: existingEnd || '', dayOffset: existingIsNextDay ? 1 : 0 };
  }
  const startM = _hhmmToMinutes(startHhmm);
  if (existingEnd) {
    if (existingIsNextDay) return { hhmm: existingEnd, dayOffset: 1 };
    if (_hhmmToMinutes(existingEnd) > startM) return { hhmm: existingEnd, dayOffset: 0 };
  }
  const step = Math.max(5, stepMinutes || 30);
  const dur = Math.max(step, Number(durationMinutes) || 30);
  const endM = startM + dur;
  if (endM < 24 * 60) {
    return { hhmm: _minutesToHhmm(endM), dayOffset: 0 };
  }
  return { hhmm: _minutesToHhmmModulo(endM), dayOffset: 1 };
}

/** User-picked end clock: later same day, or next day when clock ≤ start. */
export function endWallFromPick(startHhmm, pickedEnd) {
  if (!startHhmm) return { hhmm: pickedEnd || '', dayOffset: 0 };
  if (!pickedEnd) return endWallAfterStart(startHhmm);
  if (_hhmmToMinutes(pickedEnd) > _hhmmToMinutes(startHhmm)) {
    return { hhmm: pickedEnd, dayOffset: 0 };
  }
  return { hhmm: pickedEnd, dayOffset: 1 };
}

/**
 * Ensure end is after start. Keeps an existing end that is already later;
 * otherwise sets start + durationMinutes, wrapping past local midnight.
 */
export function endTimeAfterStart(startHhmm, opts = {}) {
  return endWallAfterStart(startHhmm, opts).hhmm;
}

// Local Y/M(0-based)/D parts of an instant in `tz`.
export function ymdParts(date, tz) {
  const [y, m, d] = isoLocalInTz(date.toISOString(), tz).slice(0, 10).split('-').map(Number);
  return { y, mo: m - 1, d };
}

// 'YYYY-MM-DD' for an ISO instant in `tz`.
export function ymdKeyInTz(iso, tz) {
  return isoLocalInTz(iso, tz).slice(0, 10);
}

// Ordered list of 'YYYY-MM-DD' day keys (in `tz`) covered by [startIso, endIso).
// The trailing instant is pulled back by one minute so an event ending exactly
// at local midnight does NOT count the next day. Single-day events return one key.
export function spanDayKeys(startIso, endIso, tz) {
  const startMs = new Date(startIso).getTime();
  const startKey = ymdKeyInTz(startIso, tz);
  let endKey = startKey;
  if (endIso) {
    const adj = new Date(endIso).getTime() - 60000;
    if (adj > startMs) endKey = ymdKeyInTz(new Date(adj).toISOString(), tz);
  }
  const keys = [];
  let [y, mo, d] = startKey.split('-').map(Number);
  let guard = 0;
  while (guard++ < 400) {
    const cur = `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    keys.push(cur);
    if (cur >= endKey) break;
    const next = new Date(Date.UTC(y, mo - 1, d + 1));
    y = next.getUTCFullYear(); mo = next.getUTCMonth() + 1; d = next.getUTCDate();
  }
  return keys;
}

// True when an event's start and end fall on different calendar days in `tz`.
export function isMultiDayEvent(event, tz) {
  if (!event || !event.start_utc || !event.end_utc) return false;
  return spanDayKeys(event.start_utc, event.end_utc, tz).length > 1;
}

// --- City / timezone labels (flight-style local time tags) ---
const TZ_CITY = {
  'Europe/Moscow': { en: 'Moscow', ru: 'Москва' },
  'Europe/Kaliningrad': { en: 'Kaliningrad', ru: 'Калининград' },
  'Europe/Samara': { en: 'Samara', ru: 'Самара' },
  'Asia/Yekaterinburg': { en: 'Yekaterinburg', ru: 'Екатеринбург' },
  'Asia/Omsk': { en: 'Omsk', ru: 'Омск' },
  'Asia/Novosibirsk': { en: 'Novosibirsk', ru: 'Новосибирск' },
  'Asia/Krasnoyarsk': { en: 'Krasnoyarsk', ru: 'Красноярск' },
  'Asia/Irkutsk': { en: 'Irkutsk', ru: 'Иркутск' },
  'Asia/Yakutsk': { en: 'Yakutsk', ru: 'Якутск' },
  'Asia/Vladivostok': { en: 'Vladivostok', ru: 'Владивосток' },
  'Asia/Magadan': { en: 'Magadan', ru: 'Магадан' },
  'Asia/Kamchatka': { en: 'Kamchatka', ru: 'Камчатка' },
  'Europe/London': { en: 'London', ru: 'Лондон' },
  'Europe/Paris': { en: 'Paris', ru: 'Париж' },
  'Europe/Berlin': { en: 'Berlin', ru: 'Берлин' },
  'Europe/Istanbul': { en: 'Istanbul', ru: 'Стамбул' },
  'Europe/Minsk': { en: 'Minsk', ru: 'Минск' },
  'Europe/Kiev': { en: 'Kyiv', ru: 'Киев' },
  'Asia/Dubai': { en: 'Dubai', ru: 'Дубай' },
  'Asia/Kolkata': { en: 'Delhi', ru: 'Дели' },
  'Asia/Calcutta': { en: 'Delhi', ru: 'Дели' },
  'Asia/Hong_Kong': { en: 'Hong Kong', ru: 'Гонконг' },
  'Asia/Shanghai': { en: 'Shanghai', ru: 'Шанхай' },
  'Asia/Singapore': { en: 'Singapore', ru: 'Сингапур' },
  'Asia/Tokyo': { en: 'Tokyo', ru: 'Токио' },
  'Asia/Almaty': { en: 'Almaty', ru: 'Алматы' },
  'Asia/Tashkent': { en: 'Tashkent', ru: 'Ташкент' },
  'Asia/Bishkek': { en: 'Bishkek', ru: 'Бишкек' },
  'Asia/Dushanbe': { en: 'Dushanbe', ru: 'Душанбе' },
  'Asia/Tbilisi': { en: 'Tbilisi', ru: 'Тбилиси' },
  'Asia/Yerevan': { en: 'Yerevan', ru: 'Ереван' },
  'Asia/Baku': { en: 'Baku', ru: 'Баку' },
  'America/New_York': { en: 'New York', ru: 'Нью-Йорк' },
  'America/Chicago': { en: 'Chicago', ru: 'Чикаго' },
  'America/Los_Angeles': { en: 'Los Angeles', ru: 'Лос-Анджелес' },
  'UTC': { en: 'UTC', ru: 'UTC' },
};

// Friendly city name for an IANA timezone (locale-aware), with a clean fallback
// to the last path segment (e.g. 'America/Sao_Paulo' -> 'Sao Paulo').
export function tzCity(tz, locale = 'en') {
  if (!tz) return '';
  const o = TZ_CITY[tz];
  if (o) return locale.startsWith('ru') ? o.ru : o.en;
  return (tz.split('/').pop() || tz).replace(/_/g, ' ');
}

// 'UTC+4' / 'UTC-3:30' style offset label for a given instant + timezone.
export function tzOffsetLabel(iso, tz) {
  const m = tzOffsetMinutes(new Date(iso), tz);
  const sign = m >= 0 ? '+' : '-';
  const a = Math.abs(m);
  const h = Math.floor(a / 60);
  const mm = a % 60;
  return `UTC${sign}${h}${mm ? ':' + String(mm).padStart(2, '0') : ''}`;
}

// Representative Russian city per whole-hour UTC offset — RU-market reference
// used as a fallback when the detected IANA zone isn't in our named-city map.
const RU_OFFSET_CITY = {
  2: { en: 'Kaliningrad', ru: 'Калининград' },
  3: { en: 'Moscow', ru: 'Москва' },
  4: { en: 'Samara', ru: 'Самара' },
  5: { en: 'Yekaterinburg', ru: 'Екатеринбург' },
  6: { en: 'Omsk', ru: 'Омск' },
  7: { en: 'Krasnoyarsk', ru: 'Красноярск' },
  8: { en: 'Irkutsk', ru: 'Иркутск' },
  9: { en: 'Yakutsk', ru: 'Якутск' },
  10: { en: 'Vladivostok', ru: 'Владивосток' },
  11: { en: 'Magadan', ru: 'Магадан' },
  12: { en: 'Kamchatka', ru: 'Камчатка' },
};

// Human, RU-friendly time-zone label like "UTC+2 · Париж" for a given zone.
// Always resolves to something readable (never a raw placeholder): uses the
// named-city map first, then a same-offset Russian reference city, then the
// raw IANA city segment — and always appends the current UTC offset.
export function tzZoneLabel(tz, locale = 'en') {
  if (!tz) return '';
  const ru = locale.startsWith('ru');
  const offMin = tzOffsetMinutes(new Date(), tz);
  const offLabel = tzOffsetLabel(new Date().toISOString(), tz);
  const known = TZ_CITY[tz];
  let city;
  if (known) {
    city = ru ? known.ru : known.en;
  } else {
    const h = offMin / 60;
    const ref = Number.isInteger(h) ? RU_OFFSET_CITY[h] : null;
    city = ref ? (ru ? ref.ru : ref.en) : (tz.split('/').pop() || tz).replace(/_/g, ' ');
  }
  return `${offLabel} · ${city}`;
}

// --- Past-date floor (Today − 24h) for date/time pickers ---
// Earliest selectable value as a SOFT floor; the 24h slack absorbs timezone
// differences between host & guest. Use as the `min` attr on native
// <input type="date"> / <input type="datetime-local"> pickers app-wide.
export function pastFloorDate() {
  return new Date(Date.now() - 24 * 60 * 60 * 1000);
}
export function minDateStr() {
  return toYMD(pastFloorDate());
}
export function minDateTimeLocalStr() {
  const d = pastFloorDate();
  const pad = (n) => String(n).padStart(2, '0');
  return `${toYMD(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Compact duration label from minutes, e.g. "2d 3h 15min" (localized unit
// suffixes). >24h → days+hours+minutes; >60m → hours+minutes; else minutes.
// Pass i18next `t` so labels follow the active locale (en: d/h/min, ru: дн/ч/мин).
export function formatDuration(minutes, t) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  const parts = [];
  if (d) parts.push(`${d}${t('quick_invite.unit_day')}`);
  if (h) parts.push(`${h}${t('quick_invite.unit_hour')}`);
  if (m || (!d && !h)) parts.push(`${m}${t('quick_invite.min')}`);
  return parts.join(' ');
}

// The browser's resolved IANA timezone (e.g. 'Asia/Dubai').
export function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

// Immediate load window: today 00:00 → start of the month AFTER next (exclusive),
// i.e. everything from today through the end of next month. Used by the Events
// list & search stage-1 to keep initial loads fast; anything outside needs search.
export function immediateWindow(tz) {
  const { y, mo, d } = ymdParts(new Date(), tz);
  const start = zonedDayStart(y, mo, d, tz);
  const end = zonedDayStart(y, mo + 2, 1, tz); // exclusive: first day two months ahead
  return { start, end };
}

/** UTC instant of local midnight for a `YYYY-MM-DD` day in `tz`. */
export function planningDateStartUtc(ymd, tz) {
  if (!ymd) return null;
  return wallTimeToUtc(`${ymd}T00:00`, tz);
}

/** Exclusive UTC instant of the next local-day start after `YYYY-MM-DD` in `tz`. */
export function planningDateEndUtc(ymd, tz) {
  if (!ymd) return null;
  const [y, mo, d] = String(ymd).split('-').map(Number);
  if (!y || !mo || !d) return null;
  return zonedDayStart(y, mo - 1, d + 1, tz);
}

/**
 * Add whole calendar days to a `YYYY-MM-DD` key using UTC date arithmetic.
 * Rejects invalid input instead of interpreting it in the browser timezone.
 */
export function addCalendarDaysYmd(ymd, days) {
  if (typeof ymd !== 'string' || !Number.isFinite(Number(days))) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return null;
  }
  const next = new Date(Date.UTC(y, mo - 1, d + Math.trunc(Number(days))));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${String(yy).padStart(4, '0')}-${mm}-${dd}`;
}

/**
 * Owner-scoped occurrence-index window for Events-list date fields.
 * `toDate` is inclusive as a calendar day; the UTC end is the next local
 * midnight so DST transitions never use a 23:59:59 wall time.
 */
export function planningOccurrenceWindow({
  fromDate, toDate, tz, fallbackStart = null, fallbackEnd = null,
} = {}) {
  let start = fallbackStart;
  let end = fallbackEnd;
  if (fromDate) {
    start = planningDateStartUtc(fromDate, tz) || fallbackStart;
  } else if (toDate) {
    const startYmd = addCalendarDaysYmd(toDate, -365);
    start = startYmd ? planningDateStartUtc(startYmd, tz) : fallbackStart;
  }
  if (toDate) {
    end = planningDateEndUtc(toDate, tz) || fallbackEnd;
  } else if (fromDate) {
    const endYmd = addCalendarDaysYmd(fromDate, 365);
    end = endYmd ? planningDateStartUtc(endYmd, tz) : fallbackEnd;
  }
  return { start, end };
}

/** Inclusive planning-tz calendar-day membership for a single instant. */
export function isoInPlanningDateRange(iso, from, to, tz) {
  if (!iso) return false;
  try {
    const y = ymdKeyInTz(iso, tz);
    if (!y) return false;
    if (from && y < from) return false;
    if (to && y > to) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether a projected occurrence overlaps the inclusive planning-tz date range.
 * End instants are treated as exclusive: an occurrence ending at local midnight
 * occupies only the previous local day.
 */
export function occurrenceOverlapsDateRange(occurrence, from, to, tz) {
  const startIso = occurrence?.start_utc;
  if (!startIso) return false;
  try {
    const start = ymdKeyInTz(startIso, tz);
    if (!start) return false;
    let end = start;
    if (occurrence?.end_utc) {
      const startMs = new Date(startIso).getTime();
      const adj = new Date(occurrence.end_utc).getTime() - 60000;
      if (Number.isFinite(adj) && adj > startMs) {
        end = ymdKeyInTz(new Date(adj).toISOString(), tz) || start;
      }
    }
    return (!to || start <= to) && (!from || end >= from);
  } catch {
    return false;
  }
}
